import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ticketService } from '../../services/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Package,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Zap
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Format event name: 002 → M2, M002 → M2
const formatEventName = (eventName) => {
  if (!eventName) return '-';
  return eventName.replace(/\b[M]?0*(\d{1,3})\b/gi, (match, num) => `M${parseInt(num, 10)}`);
};

export const AttribuerVenteModal = ({ 
  open, 
  onOpenChange, 
  venteTicket, 
  project = 'WORLD CUP 2026',
  onSuccess,
  onSpecialAttribution  // Callback to open special attribution modal
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [validateOnSubmit, setValidateOnSubmit] = useState(false);

  // Fetch available ACHATs when modal opens
  useEffect(() => {
    if (open && venteTicket?.id) {
      fetchAvailableAchats();
    } else {
      // Reset state when modal closes
      setData(null);
      setSelectedIds(new Set());
      setExpandedGroups(new Set());
      setValidateOnSubmit(false);
    }
  }, [open, venteTicket?.id]);

  const fetchAvailableAchats = async () => {
    setLoading(true);
    try {
      const result = await ticketService.getAvailableAchats(venteTicket.id, project);
      setData(result);
      
      const qty = result.vente_qty || venteTicket?.qty || 1;
      
      // Auto-expand the OPTIMAL group (exact match to qty), or first group with enough tickets
      if (result.groups?.length > 0) {
        // Find optimal group (exact match)
        const optimalGroup = result.groups.find(g => g.count === qty);
        // Or find first group with enough tickets
        const sufficientGroup = result.groups.find(g => g.count >= qty);
        
        const groupToExpand = optimalGroup || sufficientGroup || result.groups[0];
        setExpandedGroups(new Set([groupToExpand.email_compte]));
      }
    } catch (error) {
      console.error('Error fetching available achats:', error);
      toast.error('Erreur lors du chargement des ACHAT disponibles');
    } finally {
      setLoading(false);
    }
  };


  const toggleGroup = (email) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const toggleTicket = (ticketId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const selectGroupTickets = (tickets, count) => {
    const ticketIds = tickets.slice(0, count).map(t => t.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      // Remove all from this group first
      tickets.forEach(t => next.delete(t.id));
      // Add the selected count
      ticketIds.forEach(id => next.add(id));
      return next;
    });
  };

  const selectAllFromGroup = (tickets) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      tickets.forEach(t => next.add(t.id));
      return next;
    });
  };

  const deselectAllFromGroup = (tickets) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      tickets.forEach(t => next.delete(t.id));
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un ticket ACHAT');
      return;
    }

    // Block if not exact match
    if (selectedIds.size !== venteQty) {
      if (selectedIds.size < venteQty) {
        toast.error(`Il manque ${venteQty - selectedIds.size} billet(s) à attribuer. Sélectionnez exactement ${venteQty} billet(s).`);
      } else {
        toast.error(`Trop de billets sélectionnés. Sélectionnez exactement ${venteQty} billet(s).`);
      }
      return;
    }

    setSubmitting(true);
    try {
      const result = await ticketService.manualAttribution(
        venteTicket.id,
        Array.from(selectedIds),
        validateOnSubmit,
        project
      );
      
      toast.success(result.message);
      
      // Rafraîchir les données AVANT de fermer le modal
      if (onSuccess) {
        await onSuccess();
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error performing attribution:', error);
      toast.error('Erreur lors de l\'attribution');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if selection matches VENTE qty
  const venteQty = data?.vente_qty || venteTicket?.qty || 1;
  const isExactMatch = selectedIds.size === venteQty;
  const isTooMany = selectedIds.size > venteQty;
  const isTooFew = selectedIds.size > 0 && selectedIds.size < venteQty;
  const isInvalid = isTooMany || isTooFew;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-zinc-900 border-[#C4A052]/30">
        <DialogHeader>
          <DialogTitle className="text-xl text-[#C4A052] flex items-center gap-2">
            <Package className="h-5 w-5" />
            Attribution manuelle
          </DialogTitle>
        </DialogHeader>

        {/* VENTE Info */}
        {venteTicket && (
          <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded font-bold">VENTE</span>
                  <span className="text-white font-mono text-sm">{venteTicket.ref_vente}</span>
                </div>
                <p className="text-zinc-300">{formatEventName(venteTicket.evenement)}</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Catégorie: <span className="text-[#C4A052]">{venteTicket.categorie || 'N/A'}</span>
                  {' '} · {' '}
                  Client: <span className="text-zinc-300">{venteTicket.nom_client || 'N/A'}</span>
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-[#C4A052]">{venteQty}</div>
                <div className="text-xs text-zinc-400">billet(s) à attribuer</div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#C4A052]" />
            <span className="ml-3 text-zinc-400">Recherche des ACHAT compatibles...</span>
          </div>
        )}

        {/* No Results - Show special attribution option */}
        {!loading && data && data.total_available === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
            <p className="text-zinc-300 text-lg">Aucun ACHAT disponible</p>
            <p className="text-zinc-500 text-sm mt-2 mb-6">
              Aucun ticket ACHAT ne correspond à cet événement et catégorie
            </p>
            
            {onSpecialAttribution && (
              <Button
                onClick={() => {
                  onOpenChange(false);
                  onSpecialAttribution(venteTicket);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              >
                <Zap className="h-4 w-4 mr-2" />
                Attribution Spéciale (Autre catégorie)
              </Button>
            )}
          </div>
        )}

        {/* Results */}
        {!loading && data && data.total_available > 0 && (
          <>
            {/* Summary */}
            <div className="flex items-center justify-between text-sm py-2 border-b border-zinc-700">
              <span className="text-zinc-400">
                <span className="text-[#C4A052] font-bold">{data.total_available}</span> ACHAT disponible(s) 
                dans <span className="text-blue-400">{data.groups?.length}</span> compte(s)
              </span>
              <div className={cn(
                "flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold",
                isExactMatch ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50" :
                isInvalid ? "bg-red-500/20 text-red-400 ring-1 ring-red-500/50" :
                "bg-zinc-800 text-zinc-400"
              )}>
                {isExactMatch ? <CheckCircle className="h-4 w-4" /> :
                 isInvalid ? <AlertTriangle className="h-4 w-4" /> :
                 <Package className="h-4 w-4" />}
                <span>{selectedIds.size} / {venteQty} sélectionné(s)</span>
              </div>
            </div>

            {/* Groups List - Sorted: exact match first, then by count descending */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-2 py-2">
              {/* Sort groups: exact match first, then closest to venteQty, then by count */}
              {[...(data.groups || [])].sort((a, b) => {
                const aExact = a.count === venteQty;
                const bExact = b.count === venteQty;
                const aClose = a.count >= venteQty;
                const bClose = b.count >= venteQty;
                
                // Exact matches first
                if (aExact && !bExact) return -1;
                if (bExact && !aExact) return 1;
                
                // Then groups with enough tickets
                if (aClose && !bClose) return -1;
                if (bClose && !aClose) return 1;
                
                // Then by closeness to venteQty
                const aDiff = Math.abs(a.count - venteQty);
                const bDiff = Math.abs(b.count - venteQty);
                return aDiff - bDiff;
              }).map((group, index) => {
                const isExpanded = expandedGroups.has(group.email_compte);
                const selectedInGroup = group.tickets.filter(t => selectedIds.has(t.id)).length;
                const isExactMatchGroup = group.count === venteQty;
                const hasEnough = group.count >= venteQty;
                const isNotOptimal = !isExactMatchGroup && !hasEnough;
                
                // Check if any group is OPTIMAL (exact match)
                const allGroups = data.groups || [];
                const hasOptimalGroup = allGroups.some(g => g.count === venteQty);
                // First group with enough tickets is "MEILLEUR" if no OPTIMAL exists
                const isBestChoice = !hasOptimalGroup && hasEnough && index === 0;
                
                // Adjacency info from backend
                const adjacency = group.adjacency || {};
                const isAdjacent = adjacency.is_adjacent === true;
                const isSameBlock = adjacency.is_same_block === true;
                
                return (
                  <div 
                    key={group.email_compte} 
                    className={cn(
                      "border rounded-lg overflow-hidden",
                      isExactMatchGroup 
                        ? "border-emerald-500/50 ring-2 ring-emerald-500/30" 
                        : isBestChoice
                          ? "border-emerald-500/30 ring-1 ring-emerald-500/20"
                          : hasEnough 
                            ? "border-blue-500/30"
                            : "border-red-500/30"  // NOT OPTIMAL = RED
                    )}
                  >
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.email_compte)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 transition-colors",
                        isExactMatchGroup 
                          ? "bg-emerald-500/10 hover:bg-emerald-500/20" 
                          : isBestChoice
                            ? "bg-emerald-500/5 hover:bg-emerald-500/10"
                            : hasEnough
                              ? "bg-zinc-800 hover:bg-zinc-700"
                              : "bg-red-500/5 hover:bg-red-500/10"  // NOT OPTIMAL = RED BG
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? 
                          <ChevronDown className="h-4 w-4 text-zinc-400" /> : 
                          <ChevronRight className="h-4 w-4 text-zinc-400" />
                        }
                        <Users className={cn(
                          "h-4 w-4", 
                          isExactMatchGroup || isBestChoice ? "text-emerald-400" : 
                          hasEnough ? "text-blue-400" : 
                          "text-red-400"
                        )} />
                        <span className={cn(
                          "font-medium truncate max-w-[200px]",
                          isNotOptimal ? "text-red-300" : "text-white"
                        )}>
                          {group.email_compte}
                        </span>
                        {/* Show category from first ticket in group - visible without expanding */}
                        {group.tickets?.[0]?.categorie && (
                          <span className="text-xs text-[#C4A052] bg-[#C4A052]/10 px-2 py-0.5 rounded border border-[#C4A052]/30">
                            {group.tickets[0].categorie}
                          </span>
                        )}
                        {/* Badges: OPTIMAL, MEILLEUR, ADJACENTS, PAS ASSEZ */}
                        {isExactMatchGroup && (
                          <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            OPTIMAL ✓
                          </span>
                        )}
                        {isBestChoice && (
                          <span className="bg-emerald-600/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            MEILLEUR ✓
                          </span>
                        )}
                        {isAdjacent && (
                          <span className="bg-purple-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            ADJACENTS ✓
                          </span>
                        )}
                        {!isAdjacent && isSameBlock && hasEnough && !isExactMatchGroup && !isBestChoice && (
                          <span className="bg-blue-500/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            MÊME BLOC
                          </span>
                        )}
                        {isNotOptimal && (
                          <span className="bg-red-500/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            PAS ASSEZ
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {selectedInGroup > 0 && (
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">
                            {selectedInGroup} sélectionné(s)
                          </span>
                        )}
                        <span className={cn(
                          "text-sm",
                          isNotOptimal ? "text-red-400" : "text-zinc-400"
                        )}>
                          {group.count} billet(s)
                        </span>
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="bg-zinc-900/50 p-3 space-y-2">
                        {/* Quick Actions */}
                        <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                          <span className="text-xs text-zinc-500">Sélection rapide:</span>
                          {group.count >= venteQty && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); selectGroupTickets(group.tickets, venteQty); }}
                              className="h-6 text-xs bg-[#C4A052]/10 border-[#C4A052]/30 text-[#C4A052] hover:bg-[#C4A052]/20"
                            >
                              Sélectionner {venteQty}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); selectAllFromGroup(group.tickets); }}
                            className="h-6 text-xs"
                          >
                            Tout
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); deselectAllFromGroup(group.tickets); }}
                            className="h-6 text-xs"
                          >
                            Aucun
                          </Button>
                        </div>

                        {/* Tickets */}
                        <div className="space-y-1">
                          {group.tickets.map((ticket) => {
                            const isSelected = selectedIds.has(ticket.id);
                            return (
                              <div
                                key={ticket.id}
                                onClick={() => toggleTicket(ticket.id)}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-2 rounded cursor-pointer transition-colors",
                                  isSelected 
                                    ? "bg-emerald-500/20 border border-emerald-500/30" 
                                    : "bg-zinc-800/50 hover:bg-zinc-800 border border-transparent"
                                )}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleTicket(ticket.id)}
                                  className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-zinc-300">
                                      {formatEventName(ticket.evenement)}
                                    </span>
                                    <span className="text-xs text-zinc-500">·</span>
                                    <span className="text-xs text-[#C4A052]">
                                      {ticket.categorie || 'N/A'}
                                    </span>
                                  </div>
                                  {ticket.block && (
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                      Block: {ticket.block} · Rang: {ticket.rang || '-'} · Place: {ticket.place || '-'}
                                    </div>
                                  )}
                                </div>
                                {isSelected && (
                                  <Check className="h-4 w-4 text-emerald-400" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-zinc-700 pt-4 space-y-3">
              {/* Validate Option */}
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={validateOnSubmit}
                  onCheckedChange={setValidateOnSubmit}
                  className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                />
                <span className="text-sm text-zinc-300">
                  Marquer comme <span className="text-emerald-400 font-semibold">VALIDÉ</span> (protégé contre les modifications)
                </span>
              </label>

              {/* Warning if too many selected - BLOCKS submission */}
              {isTooMany && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <span>
                    <strong>BLOQUÉ:</strong> Vous avez sélectionné {selectedIds.size} billets mais la VENTE n'en demande que {venteQty}. 
                    Désélectionnez {selectedIds.size - venteQty} billet(s) pour continuer.
                  </span>
                </div>
              )}

              {/* Warning if not enough selected - BLOCKS submission */}
              {isTooFew && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <span>
                    <strong>INCOMPLET:</strong> Il manque {venteQty - selectedIds.size} billet(s). 
                    Sélectionnez exactement {venteQty} billet(s) pour valider.
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between gap-3">
                {/* Special Attribution Button - ALWAYS visible when callback exists */}
                {onSpecialAttribution && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      onSpecialAttribution(venteTicket);
                    }}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Attribution Spéciale
                  </Button>
                )}
                
                <div className="flex items-center gap-3 ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={submitting}
                    className="border-zinc-600 text-zinc-300"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || selectedIds.size === 0 || isInvalid}
                    className={cn(
                      "font-semibold",
                      isInvalid 
                        ? "bg-red-500/50 text-red-200 cursor-not-allowed border border-red-500/50"
                        : isExactMatch 
                          ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                          : "bg-zinc-600 text-zinc-400 cursor-not-allowed"
                    )}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Attribution...
                      </>
                    ) : isTooMany ? (
                      <>
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Trop de billets ({selectedIds.size}/{venteQty})
                      </>
                    ) : isTooFew ? (
                      <>
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Il manque {venteQty - selectedIds.size} billet(s)
                      </>
                    ) : isExactMatch ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Attribuer {selectedIds.size} ACHAT(s)
                      </>
                    ) : (
                      <>
                        <Package className="h-4 w-4 mr-2" />
                        Sélectionnez {venteQty} billet(s)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
